import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { getApiUrl, getAuthHeaders, getAuthHeadersForFormData } from '../../config/api.config';

const initialState = {
  sermons: [],
  currentSermon: null,
  loading: false,
  error: null,
  success: false,
  totalPages: 1,
  currentPage: 1
};

// Get all sermons
export const getAllSermons = createAsyncThunk(
  'sermons/getAll',
  async ({ page = 1, limit = 10, category, search } = {}, { rejectWithValue }) => {
    try {
      let url = `/sermons?page=${page}&limit=${limit}`;
      if (category) url += `&category=${category}`;
      if (search) url += `&search=${search}`;
      
      const response = await axios.get(getApiUrl(url));
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch sermons'
      );
    }
  }
);

// Get single sermon
export const getSermon = createAsyncThunk(
  'sermons/getOne',
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.get(getApiUrl(`/sermons/${id}`));
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch sermon'
      );
    }
  }
);

// Create sermon (Admin)
export const createSermon = createAsyncThunk(
  'sermons/create',
  async (sermonData, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        getApiUrl('/sermons'),
        sermonData,
        { headers: getAuthHeadersForFormData() }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to create sermon'
      );
    }
  }
);

// Update sermon (Admin)
export const updateSermon = createAsyncThunk(
  'sermons/update',
  async ({ id, sermonData }, { rejectWithValue }) => {
    try {
      const response = await axios.put(
        getApiUrl(`/sermons/${id}`),
        sermonData,
        { headers: getAuthHeadersForFormData() }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to update sermon'
      );
    }
  }
);

// Delete sermon (Admin)
export const deleteSermon = createAsyncThunk(
  'sermons/delete',
  async (id, { rejectWithValue }) => {
    try {
      await axios.delete(
        getApiUrl(`/sermons/${id}`),
        { headers: getAuthHeaders() }
      );
      return id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to delete sermon'
      );
    }
  }
);

// Increment download count
export const incrementDownload = createAsyncThunk(
  'sermons/incrementDownload',
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.post(getApiUrl(`/sermons/${id}/download`));
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to increment download'
      );
    }
  }
);

const sermonSlice = createSlice({
  name: 'sermons',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = false;
    },
    clearCurrentSermon: (state) => {
      state.currentSermon = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Get all sermons
      .addCase(getAllSermons.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllSermons.fulfilled, (state, action) => {
        state.loading = false;
        state.sermons = action.payload.data.sermons;
        state.totalPages = action.payload.data.totalPages || 1;
        state.currentPage = action.payload.data.currentPage || 1;
      })
      .addCase(getAllSermons.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Get single sermon
      .addCase(getSermon.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getSermon.fulfilled, (state, action) => {
        state.loading = false;
        state.currentSermon = action.payload.data.sermon;
      })
      .addCase(getSermon.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create sermon
      .addCase(createSermon.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createSermon.fulfilled, (state, action) => {
        state.loading = false;
        state.sermons.unshift(action.payload.data.sermon);
        state.success = true;
      })
      .addCase(createSermon.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update sermon
      .addCase(updateSermon.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateSermon.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.sermons.findIndex(s => s._id === action.payload.data.sermon._id);
        if (index !== -1) {
          state.sermons[index] = action.payload.data.sermon;
        }
        state.success = true;
      })
      .addCase(updateSermon.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete sermon
      .addCase(deleteSermon.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteSermon.fulfilled, (state, action) => {
        state.loading = false;
        state.sermons = state.sermons.filter(s => s._id !== action.payload);
        state.success = true;
      })
      .addCase(deleteSermon.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const { clearError, clearSuccess, clearCurrentSermon } = sermonSlice.actions;
export default sermonSlice.reducer;
